/**
 * Shared color palettes for all canvas node toolbars.
 *
 * One roster of ten families (the kit's STICKY_VARIANTS order, Poppy first),
 * each pairing three stops — the design system's canvas palette, experiment
 * 19 candidate B (DECISIONS D41):
 *
 *   fill  `--sticky-*`          paper: notes, shape bodies, Section panels;
 *                                mode-constant.
 *   dark  `--spectrum-*-dark`   what sits ON a fill: a Section's border, a
 *                                filled shape's outline, chosen text on a
 *                                fill; mode-constant, the fill never inverts.
 *   mark  `--mark-*`            what sits on the canvas GROUND: text labels,
 *                                edge and connector lines, no-fill outlines,
 *                                drawn ink, line labels; one value per mode.
 *
 * Palette tiers:
 *   SHAPE_COLORS     — Shape fills. value = fill; swatchHex = dark stop.
 *   SECTION_COLORS   — Section fills. value = fill; swatchHex = dark stop.
 *   NOTE_COLORS      — Text note backgrounds. Fills only.
 *   EDGE_COLORS      — Edge / connector lines and drawn ink. value = mark;
 *                      Neutral leads.
 *   LABEL_COLORS     — Text label colors. value = mark; Default leads.
 *
 * Stored canvases carry the dark stops marks used to be — the renderers
 * resolve them through `canvasColorRoles` on load (no migration).
 */

export interface CanvasColor {
  name: string;
  /** Hex or CSS var shown in the color swatch circle */
  swatchHex: string;
  /** Value written to Loro node data */
  value: string;
  /**
   * Swatch drawing, the design-system recipe (specs/canvas-toolbars ColorRow):
   * the circle's centre is the light fill tint, its ring the paired dark
   * stroke. When absent the swatch derives both from `swatchHex`.
   */
  swatchFill?: string;
  swatchStroke?: string;
}

export interface CanvasColorFamily {
  key: string;
  name: string;
  fill: string;
  dark: string;
  mark: string;
}

const family = (key: string, name: string, hue: string): CanvasColorFamily => ({
  key,
  name,
  fill: `var(--sticky-${key})`,
  dark: `var(--spectrum-${hue}-dark)`,
  mark: `var(--mark-${key})`,
});

/** The roster — order matches the kit's STICKY_VARIANTS. */
export const CANVAS_COLOR_FAMILIES: readonly CanvasColorFamily[] = [
  family("poppy", "Poppy", "red"),
  family("apricot", "Apricot", "orange"),
  family("butter", "Butter", "yellow"),
  family("meadow", "Meadow", "green"),
  family("sea", "Sea", "blue"),
  family("orchid", "Orchid", "violet"),
  family("rose", "Rose", "mulberry"),
  family("clay", "Clay", "brown"),
  family("mist", "Mist", "indigo"),
  family("oat", "Oat", "olive"),
];

const familyByKey = (key: string): CanvasColorFamily => {
  const f = CANVAS_COLOR_FAMILIES.find((x) => x.key === key);
  if (!f) throw new Error(`no canvas color family "${key}"`);
  return f;
};

// A fill swatch draws the tint with its dark stop as the ring; a mark swatch
// draws the mark. `swatchFill`/`swatchStroke` keep the legacy picker's
// tint-and-ring recipe fed.
const stickyFill = (f: CanvasColorFamily): CanvasColor => ({
  name: f.name,
  swatchHex: f.fill,
  value: f.fill,
  swatchFill: f.fill,
  swatchStroke: f.dark,
});

const stickyFillWithStrokeSwatch = (f: CanvasColorFamily): CanvasColor => ({
  name: f.name,
  swatchHex: f.dark,
  value: f.fill,
  swatchFill: f.fill,
  swatchStroke: f.dark,
});

const mark = (f: CanvasColorFamily): CanvasColor => ({
  name: f.name,
  swatchHex: f.mark,
  value: f.mark,
  swatchFill: f.fill,
  swatchStroke: f.mark,
});

const BUTTER = familyByKey("butter");
const OAT = familyByKey("oat");

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** Butter — default shape fill. Must match an entry in SHAPE_COLORS. */
export const DEFAULT_SHAPE_COLOR = BUTTER.fill;

/** Matches showcase SHAPE_COLOR_PALETTE `none` — transparent fill swatch. */
export const CANVAS_NO_FILL_VALUE = "transparent";

/** @deprecated Use CANVAS_NO_FILL_VALUE */
export const SHAPE_NO_FILL_VALUE = CANVAS_NO_FILL_VALUE;

export const CANVAS_NO_FILL_COLOR: CanvasColor = {
  name: "No fill",
  swatchHex: "var(--n-90)",
  value: CANVAS_NO_FILL_VALUE,
};

/** @deprecated Use CANVAS_NO_FILL_COLOR */
export const SHAPE_NO_FILL_COLOR = CANVAS_NO_FILL_COLOR;

export const SHAPE_COLORS: CanvasColor[] = [
  ...CANVAS_COLOR_FAMILIES.map(stickyFillWithStrokeSwatch),
  CANVAS_NO_FILL_COLOR,
];

// ---------------------------------------------------------------------------
// Edges, connectors and drawn ink
// ---------------------------------------------------------------------------

/** The neutral mark — default edge / connector line and pen ink. */
export const DEFAULT_EDGE_COLOR = "var(--mark-neutral)";

export const EDGE_COLORS: CanvasColor[] = [
  { name: "Neutral", swatchHex: DEFAULT_EDGE_COLOR, value: DEFAULT_EDGE_COLOR },
  ...CANVAS_COLOR_FAMILIES.map(mark),
];

// ---------------------------------------------------------------------------
// Text labels
// ---------------------------------------------------------------------------

export const DEFAULT_LABEL_COLOR = "default";

export const LABEL_COLORS: CanvasColor[] = [
  {
    name: "Default",
    swatchHex: "hsl(var(--foreground))",
    value: DEFAULT_LABEL_COLOR,
  },
  ...CANVAS_COLOR_FAMILIES.map(mark),
];

// ---------------------------------------------------------------------------
// Text notes
// ---------------------------------------------------------------------------

/** Butter — default text note background. Must match an entry in NOTE_COLORS. */
export const DEFAULT_NOTE_COLOR = BUTTER.fill;

export const NOTE_COLORS: CanvasColor[] = CANVAS_COLOR_FAMILIES.map(stickyFill);

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** Oat — default section fill. Must match an entry in SECTION_COLORS. */
export const DEFAULT_SECTION_COLOR = OAT.fill;

export const SECTION_COLORS: CanvasColor[] = [
  ...CANVAS_COLOR_FAMILIES.map(stickyFillWithStrokeSwatch),
  CANVAS_NO_FILL_COLOR,
];
